import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  PencilRuler, Calculator, LayoutDashboard, Layers, ShieldCheck, Gauge,
  Coins, FileStack, ArrowRight,
} from "lucide-react";

const features = [
  { icon: PencilRuler, title: "Smart Configurator + Canvas", desc: "Configure type, size, rooms, style and budget — or draw your floor plan on an interactive blueprint canvas with grid snapping." },
  { icon: Calculator, title: "AI Cost Estimation", desc: "Instant national-average estimates with material vs labor split across 10+ scopes of work — foundation to finishes." },
  { icon: Layers, title: "Full Scope of Work", desc: "Every build broken into contractor-grade line items you can review, compare and share." },
  { icon: LayoutDashboard, title: "Customer Portal", desc: "Save unlimited projects, revisit blueprints, and track your whole portfolio's value in one dashboard." },
  { icon: Gauge, title: "Re-estimate Anytime", desc: "Change the spec and regenerate pricing in seconds as your plans evolve." },
  { icon: Coins, title: "Flexible Pricing", desc: "Free to start, upgrade to Pro/Business for unlimited builds, or buy one-time estimate credits." },
  { icon: FileStack, title: "Every Building Type", desc: "Homes, apartments, warehouses, commercial offices, retail and industrial facilities." },
  { icon: ShieldCheck, title: "Regional Accuracy", desc: "Pricing adjusts for region and finish level so budgets reflect your local market." },
];

export default function Features() {
  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">Everything you need to build</p>
          <h1 className="mt-2 max-w-3xl font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
            From napkin sketch to build-ready budget.
          </h1>
          <p className="mt-5 max-w-xl text-lg text-muted-foreground">
            Costobuild packs the design, estimating and management tools of a full architecture office into one fast portal.
          </p>
        </motion.div>

        <div className="mt-14 grid border border-border sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06 }}
              className="border-b border-r border-border p-7 transition-colors hover:bg-secondary/20"
              data-testid={`feature-${i}`}>
              <div className="mb-4 flex h-11 w-11 items-center justify-center border border-border bg-secondary/40">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Link to="/register" data-testid="features-cta">
            <Button size="lg" className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              Start Free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/pricing">
            <Button size="lg" variant="outline" className="rounded-sm border-border font-mono2 uppercase tracking-widest">
              See Pricing
            </Button>
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
